import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

export type AnchorKey =
  | 'construction-kinshasa'
  | 'genie-civil-rdc'
  | 'travaux-publics-congo'
  | 'references-kinshasa'
  | 'chantiers-en-cours'
  | 'actualites-chantier'
  | 'travaux-publics-kinshasa'
  | 'recrutement'
  | 'internship'
  | 'devis-kinshasa'
  | 'contact-kinshasa';

interface AnchorDef {
  to: string;
  fr: string;
  en: string;
}

/**
 * Maillage interne à ancres orientées requêtes locales (Kinshasa / RDC / Congo).
 * Utilisé sur l'accueil, /services, /references et /careers pour densifier
 * les liens internes entre les pages stratégiques.
 */
const ANCHORS: Record<AnchorKey, AnchorDef> = {
  'construction-kinshasa': {
    to: '/services/construction-batiments',
    fr: 'Entreprise de construction à Kinshasa',
    en: 'Construction company in Kinshasa',
  },
  'genie-civil-rdc': {
    to: '/services/genie-civil-travaux-publics',
    fr: 'Génie civil en RDC',
    en: 'Civil engineering in DRC',
  },
  'travaux-publics-congo': {
    to: '/services/voirie-reseaux-divers',
    fr: 'Travaux publics au Congo (voirie & réseaux)',
    en: 'Public works in Congo (roads & utilities)',
  },
  'references-kinshasa': {
    to: '/references',
    fr: 'Chantiers réalisés à Kinshasa',
    en: 'Projects delivered in Kinshasa',
  },
  'chantiers-en-cours': {
    to: '/references#chantiers-en-cours',
    fr: 'Actualités chantier : réalisations en cours',
    en: 'Site updates: works in progress',
  },
  'actualites-chantier': {
    to: '/actualites',
    fr: 'Actualités chantier à Kinshasa et en RDC',
    en: 'Construction site updates in Kinshasa and DRC',
  },
  'travaux-publics-kinshasa': {
    to: '/services/genie-civil-travaux-publics',
    fr: 'Travaux publics à Kinshasa : routes, ouvrages, réseaux',
    en: 'Public works in Kinshasa: roads, structures, utilities',
  },
  recrutement: {
    to: '/careers#recrutement',
    fr: 'MTB recrutement : postes BTP en RDC',
    en: 'MTB recruitment: construction jobs in DRC',
  },
  internship: {
    to: '/careers#internship',
    fr: 'MTB internship : stages et apprentissage BTP',
    en: 'MTB internship: construction internships',
  },
  'devis-kinshasa': {
    to: '/quote-request',
    fr: 'Demander un devis construction à Kinshasa',
    en: 'Request a construction quote in Kinshasa',
  },
  'contact-kinshasa': {
    to: '/contact',
    fr: 'Contacter notre bureau à Kinshasa',
    en: 'Contact our Kinshasa office',
  },
};

interface LocalAnchorLinksProps {
  keys: AnchorKey[];
  /** Titre de section (facultatif) */
  title?: { fr: string; en: string };
  className?: string;
}

const LocalAnchorLinks = ({ keys, title, className = '' }: LocalAnchorLinksProps) => {
  const { language } = useLanguage();
  const fr = language === 'fr';
  const items = keys.map((k) => ANCHORS[k]).filter(Boolean);

  const heading =
    title ?? {
      fr: 'Explorer nos expertises BTP à Kinshasa et en RDC',
      en: 'Explore our construction expertise in Kinshasa and DRC',
    };

  return (
    <nav
      aria-label={fr ? 'Liens internes thématiques' : 'Thematic internal links'}
      className={`py-12 bg-secondary border-t border-border ${className}`}
    >
      <div className="container mx-auto px-4 max-w-5xl">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground mb-6">
          {fr ? heading.fr : heading.en}
        </h2>
        <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((item) => (
            <li key={item.to}>
              <Link
                to={item.to}
                className="group flex items-start gap-2 p-4 rounded-lg bg-card border border-border hover:border-accent/60 transition-colors text-sm font-medium text-foreground"
              >
                <ArrowRight
                  className="w-4 h-4 text-accent mt-0.5 flex-shrink-0 group-hover:translate-x-0.5 transition-transform"
                  aria-hidden="true"
                />
                <span className="group-hover:text-accent transition-colors">
                  {fr ? item.fr : item.en}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
};

export default LocalAnchorLinks;
